import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, FileText, ExternalLink } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

/**
 * Reusable list page for Institute Important Links, Notices/Circulars, and Admission Notices.
 *
 * Props:
 *  - title: page title
 *  - subtitle: short description string
 *  - eyebrow: small uppercase label above title
 *  - listUrl: full GET URL incl. query string (e.g. "/institute/notices?type=admission")
 *  - mutateBaseUrl: base URL for POST/PUT/DELETE (e.g. "/institute/notices")
 *  - extraPayload: object merged into POST/PUT body (e.g. { notice_type: "admission" })
 *  - testidPrefix: unique data-testid prefix
 *  - itemNoun: singular label used in dialog (e.g. "Link", "Notice")
 *  - uploadKind: FileUploader kind ("inst_link_pdf" or "inst_notice_pdf")
 */
export default function LinkListPage({
  title,
  subtitle,
  eyebrow,
  listUrl,
  mutateBaseUrl,
  extraPayload = {},
  testidPrefix,
  itemNoun = "Link",
  uploadKind = "inst_link_pdf",
}) {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    status: "Active",
    source: "pdf", // "pdf" | "link"
    pdf_url: "",
    pdf_public_id: "",
    external_url: "",
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listUrl]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: "",
      status: "Active",
      source: "pdf",
      pdf_url: "",
      pdf_public_id: "",
      external_url: "",
    });
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name || "",
      status: it.status || "Active",
      source: it.external_url ? "link" : "pdf",
      pdf_url: it.pdf_url || "",
      pdf_public_id: it.pdf_public_id || "",
      external_url: it.external_url || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (form.name.length > 100) {
      toast.error("Name must be 100 characters or fewer");
      return;
    }
    if (form.source === "pdf" && !form.pdf_url) {
      toast.error("Please upload a PDF or switch to External Link");
      return;
    }
    if (form.source === "link") {
      const ext = (form.external_url || "").trim();
      if (!ext) {
        toast.error("External link is required");
        return;
      }
      if (!/^https?:\/\//i.test(ext)) {
        toast.error("Link must start with http:// or https://");
        return;
      }
      if (ext.length > 500) {
        toast.error("Link is too long (max 500 characters)");
        return;
      }
    }
    setSaving(true);
    try {
      // Send fields based on chosen source; clear the other so backend stores clean data
      const payload = {
        name: form.name,
        status: form.status,
        pdf_url: form.source === "pdf" ? form.pdf_url : "",
        pdf_public_id: form.source === "pdf" ? form.pdf_public_id : "",
        external_url: form.source === "link" ? form.external_url.trim() : "",
        ...extraPayload,
      };
      if (editing) {
        const { data } = await api.put(`${mutateBaseUrl}/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success(`${itemNoun} updated`);
      } else {
        const { data } = await api.post(mutateBaseUrl, payload);
        setItems((arr) => [data, ...arr]);
        toast.success(`${itemNoun} added`);
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const it = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`${mutateBaseUrl}/${it.id}`);
      setItems((arr) => arr.filter((x) => x.id !== it.id));
      toast.success(`${itemNoun} removed`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto" data-testid={`${testidPrefix}-page`}>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">{eyebrow}</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">{title}</h1>
          {subtitle && (
            <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">{subtitle}</p>
          )}
        </div>
        {canEdit && (
          <button
            onClick={openCreate}
            data-testid={`${testidPrefix}-add-button`}
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm"
          >
            <Plus size={14} strokeWidth={1.5} /> Add {itemNoun}
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
          data-testid={`${testidPrefix}-empty`}
        >
          <FileText size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">
            No {itemNoun.toLowerCase()}s yet
          </div>
          <div className="mt-2 text-sm text-[#6b655b]">
            {canEdit
              ? `Click "Add ${itemNoun}" to upload the first one.`
              : "Your Institute Admin hasn't added any yet."}
          </div>
        </div>
      ) : (
        <div
          className="bg-white border border-[#e5e0d8] rounded-sm overflow-hidden"
          data-testid={`${testidPrefix}-table-wrapper`}
        >
          <table className="w-full text-sm">
            <thead className="bg-[#f9f8f6] border-b border-[#e5e0d8]">
              <tr>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
                  Name
                </th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold w-32">
                  Status
                </th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold w-32">
                  Source
                </th>
                {canEdit && (
                  <th className="text-right px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold w-32">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  data-testid={`${testidPrefix}-row-${it.id}`}
                  className="border-b border-[#e5e0d8] last:border-b-0 hover:bg-[#f9f8f6]/40"
                >
                  <td className="px-4 py-3 text-[#1c1a17]">{it.name}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 rounded-sm border ${
                        it.status === "Active"
                          ? "border-[#234333]/40 text-[#234333] bg-[#234333]/5"
                          : "border-[#9e2a2b]/40 text-[#9e2a2b] bg-[#9e2a2b]/5"
                      }`}
                    >
                      {it.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {it.pdf_url ? (
                      <a
                        href={it.pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`${testidPrefix}-view-${it.id}`}
                        className="inline-flex items-center gap-1 text-[#234333] hover:underline text-xs"
                      >
                        PDF <ExternalLink size={11} strokeWidth={1.5} />
                      </a>
                    ) : it.external_url ? (
                      <a
                        href={it.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`${testidPrefix}-view-${it.id}`}
                        className="inline-flex items-center gap-1 text-[#234333] hover:underline text-xs"
                        title={it.external_url}
                      >
                        Link <ExternalLink size={11} strokeWidth={1.5} />
                      </a>
                    ) : (
                      <span className="text-[#6b655b] text-xs">—</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => openEdit(it)}
                          data-testid={`${testidPrefix}-edit-${it.id}`}
                          className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                        >
                          <Pencil size={12} strokeWidth={1.5} /> Edit
                        </button>
                        <button
                          onClick={() => setConfirmDelete(it)}
                          data-testid={`${testidPrefix}-delete-${it.id}`}
                          className="inline-flex items-center gap-1 text-xs text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                        >
                          <Trash2 size={12} strokeWidth={1.5} /> Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-xl"
          data-testid={`${testidPrefix}-dialog`}
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? `Edit ${itemNoun}` : `Add ${itemNoun}`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Name *
              </div>
              <input
                data-testid={`${testidPrefix}-name-input`}
                className={inputCls}
                maxLength={100}
                placeholder="e.g., AICTE Approval 2024-25"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              <div className="mt-1 text-[11px] text-[#6b655b]">
                {form.name.length}/100 characters
              </div>
            </label>

            <label className="block">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Status *
              </div>
              <select
                data-testid={`${testidPrefix}-status-select`}
                className={inputCls}
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </label>

            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                Source *
              </div>
              <div
                className="flex gap-2 mb-3"
                data-testid={`${testidPrefix}-source-toggle`}
                role="radiogroup"
              >
                {[
                  { v: "pdf", label: "Upload PDF" },
                  { v: "link", label: "External Link" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    role="radio"
                    aria-checked={form.source === opt.v}
                    onClick={() => setForm((f) => ({ ...f, source: opt.v }))}
                    data-testid={`${testidPrefix}-source-${opt.v}`}
                    className={`flex-1 text-sm px-3 py-2 rounded-sm border transition-colors ${
                      form.source === opt.v
                        ? "bg-[#234333] text-white border-[#234333]"
                        : "bg-white text-[#1c1a17] border-[#e5e0d8] hover:bg-[#f9f8f6]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {form.source === "pdf" ? (
                <FileUploader
                  kind={uploadKind}
                  label="PDF"
                  url={form.pdf_url}
                  testid={`${testidPrefix}-pdf`}
                  onChange={({ url, public_id }) =>
                    setForm((f) => ({ ...f, pdf_url: url, pdf_public_id: public_id }))
                  }
                />
              ) : (
                <label className="block">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
                    External URL *
                  </div>
                  <input
                    data-testid={`${testidPrefix}-external-url-input`}
                    type="url"
                    className={inputCls}
                    placeholder="https://example.com/notice.pdf"
                    maxLength={500}
                    value={form.external_url}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, external_url: e.target.value }))
                    }
                  />
                  <div className="mt-1 text-[11px] text-[#6b655b]">
                    Must start with http:// or https:// — opens in a new tab when clicked.
                  </div>
                </label>
              )}
            </div>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid={`${testidPrefix}-cancel-button`}
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid={`${testidPrefix}-save-button`}
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Delete this {itemNoun.toLowerCase()}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              "{confirmDelete?.name}" will be permanently removed{confirmDelete?.pdf_url ? " and the PDF deleted from storage" : ""}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid={`${testidPrefix}-delete-confirm`}
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
